import React, {useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import pick from 'lodash/pick';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import useDebouncedState from '../../hooks/useDebouncedState';
import { formatReferrerName } from '../../helpers/referrers';
import { useGetReferrersQuery } from '../../redux/services/referrerApi';
import CustomInputLabel from '../common/CustomInputLabel';
import CreateReferrerButton from './CreateReferrerButton';

const getOptions = referrers => (
  (referrers || []).map(r => ({
    label: formatReferrerName(r),
    value: r.id,
  }))
);

const handleTextChange = ({setTextInput, setQuery, limit = 100}) => e => {
  const value = get(e, 'target.value', '');
  setTextInput(value);
  setQuery({text: value, limit});
};

const FindReferrerInput = props => {
  const {onSubmit, limit} = props;
  const [textInput, setTextInput] = useState('');
  const [selectedReferrerId, setSelectedReferrerId] = useState(props.defaultValue);
  const [query, setQuery] = useState({limit});
  const [debouncedQuery, setDebouncedQuery] = useState({limit});
  useDebouncedState(query, setDebouncedQuery, 500);
  const selectedReferrerQuery = useGetReferrersQuery(
    {id: selectedReferrerId},
    {skip: !selectedReferrerId}
  );
  const selectedReferrer = selectedReferrerQuery?.data?.data?.[0];
  const referrersQuery = useGetReferrersQuery(
    {...debouncedQuery},
    {skip: !get(debouncedQuery, 'text')}
  );
  const isLoading = referrersQuery.isLoading || selectedReferrerQuery.isLoading;
  const textChangeHandler = handleTextChange({limit, setTextInput, setQuery});
  const debouncedTextChangeHandler = useCallback(
    debounce(textChangeHandler, 500),
    [textChangeHandler]
  );
  const referrers = (get(referrersQuery, 'data.data') || []);
  const options = textInput ? getOptions(referrers) : [];
  if (selectedReferrerId) {
    return (
      <Box sx={{minWidth: props.minWidth, minHeight: props.minHeight}}>
        <Stack p
          alignItems={props?.align || 'flex-start'}
          padding={props.padding}
        >
          {props.label ? <CustomInputLabel>{props.label}</CustomInputLabel> : null}
          <Chip
            color={props?.disabled ? 'default' : 'default'}
            variant={props?.variant || undefined}
            label={isLoading ? 'Loading...' : formatReferrerName(selectedReferrer)}
            onDelete={props.disabled ? undefined : () => {
              setTextInput('');
              setSelectedReferrerId(null);
              onSubmit({target: {value: null}}, null);
              setQuery({});
            }}
          >
          </Chip>
        </Stack>
      </Box>
    );
  }
  return (
    <Stack>
      {props.label ? <CustomInputLabel>{props.label}</CustomInputLabel> : null}
      <Autocomplete
        type='text'
        style={{minWidth: props.minWidth || null, maxWidth: props.maxWidth || null}}
        options={options}
        filterOptions={x => x}
        loading={isLoading}
        onChange={(e,val,reason) => {
          if (reason === 'selectOption') {
            setSelectedReferrerId(val?.id || val?.value);
            if (val.id) return onSubmit(e, val.id);
            if (val.value) return onSubmit(e, val.value);
          }
          debouncedTextChangeHandler(e);
        }}
        renderInput={params => (
          <TextField
            placeholder={props.placeholder}
            onChange={debouncedTextChangeHandler}
            {...params}
            sx={props.sx}
            size={props.size}
            fullWidth={props.fullWidth}
            className={`${props.rounded && 'custom-text-field-rounded'}`}
            minWidth={props.minWidth}
            InputProps={{
              ...params.InputProps,
              autoFocus: props.autoFocus,
              startAdornment: <InputAdornment position="start">
                <SearchIcon style={{color: config().colors.primary, fontSize: '1.25rem', marginLeft: '4px'}} />
              </InputAdornment>,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
      )}
        {...pick(
          props, 
          'id', 'placeholder', 'freeSolo', 'disableClearable', 'autoFocus', 'onSubmit',
          'size',
          'fullWidth',
          'minWidth'
        )}
      />
      {props.allowCreateNew && 
        <CreateReferrerButton 
          afterSubmit={(e, id) => {
            setSelectedReferrerId(id);
            onSubmit(e, id)
          }}
        />
      }
    </Stack>
  );

};

FindReferrerInput.defaultProps = {
  autoFocus: false,
  defaultValue: null,
  disableClearable: false,
  freeSolo: true,
  allowCreateNew: true,
  id: 'FindReferrerInput.Autocomplete',
  placeholder: 'Search Lead Sources',
  onSubmit: ({referrerId}) => console.log('FindReferrerInput.onSubmit', referrerId),
  size: 'small',
  minHeight: '28px',
  fullWidth: true,
  padding: 0.5,
  limit: 100,
};

FindReferrerInput.propTypes = {
  autoFocus: PropTypes.bool,
  defaultValue: PropTypes.string,
  disableClearable: PropTypes.bool,
  freeSolo: PropTypes.bool,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  onSubmit: PropTypes.func,
  size: PropTypes.string,
  variant: PropTypes.string, // chip variant
};

export default FindReferrerInput;